/**
 * Image Search Agent
 *
 * Sub-agent for searching images from multiple sources:
 * - Brandfetch (logos and brand colors)
 * - Wikimedia Commons (headshots, product images, general)
 * - Current presentation (existing images)
 *
 * Implements the Agent-as-Tool pattern via AgentBaseClass.
 *
 * ## Asset ID Formats
 * - logo_*: Logos from Brandfetch (e.g., "logo_abc12")
 * - color_*: Brand colors from Brandfetch (e.g., "color_xyz78")
 * - wikimedia_*: Images from Wikimedia Commons (e.g., "wikimedia_def45")
 * - Composite IDs: Images from presentation slides (e.g., "deck_abc|slide_257|shape_42")
 *
 * ## Usage
 * ```typescript
 * const result = await imageSearchAgent.execute({
 *     requirements: [
 *         { id: 'A', requirement: 'Microsoft logo in white variant' },
 *         { id: 'B', requirement: 'Satya Nadella headshot' }
 *     ],
 *     documentId: 0  // Optional: search current presentation
 * }, callbacks);
 * ```
 */
import { AgentBaseClass } from "../../base/agentBaseClass";
import { ToolDescription } from "../../base/toolDescription";
import { ImageSearchParamsSchema } from "./image.schema";
import { SearchBrandfetchTool, RetrieveBrandfetchTool, SearchWikimediaTool, RetrieveWikimediaTool, SearchPresentationImagesTool, RetrievePresentationImageTool, } from "./tools";
import { ContextResolver } from "../../../core/contextResolver";
import { backendClient } from "../../../../services/communication/backendClient";
import GlobalStateManager from "../../../globals/global-state/globalStateManager";
import { buildImageSearchSystemPrompt } from "./prompt";
// ═══════════════════════════════════════════════════════════════════════════
// IMAGE SEARCH AGENT
// ═══════════════════════════════════════════════════════════════════════════
// System prompt for agent-as-tool description (stored in Keywords AI)
const IMAGE_SEARCH_AGENT_TOOL_PROMPT = `Search for logos, brand colors, executive headshots, and product images.

Use this agent when you need:
- Company logos (Brandfetch → presentation → Wikimedia)
- Brand colors (Brandfetch)
- Executive headshots (presentation → Wikimedia)
- Product images (presentation → Wikimedia)

The agent searches appropriate sources, downloads images, and returns asset IDs:
- logo_*: Logos from Brandfetch
- color_*: Brand colors
- wikimedia_*: Images from Wikimedia Commons

**Structured preferences per requirement (optional):**
- \`preferredShape\`: "square" (icon, ~1:1) | "horizontal" (wide logo with text) | "any"
- \`preferredBackground\`: "light_bg" (need dark logo) | "dark_bg" (need light logo) | "any"

If a preference can't be matched, the agent still returns the best available asset but flags the mismatch in the result via \`preferenceMismatch\`. Check this field to know if the returned logo doesn't match what was requested.`;
/**
 * Image Search Agent
 *
 * Searches for logos, brand colors, executive headshots, product images,
 * and presentation graphics from multiple sources.
 */
export class ImageSearchAgent extends AgentBaseClass {
    constructor() {
        super();
        // ═══════════════════════════════════════════════════════════════════════
        // AGENT-AS-TOOL PROMPT CONFIGURATION
        // ═══════════════════════════════════════════════════════════════════════
        /** System prompt description for agent-as-tool */
        this.systemPromptDescription = IMAGE_SEARCH_AGENT_TOOL_PROMPT;
        // ═══════════════════════════════════════════════════════════════════════
        // REQUIRED IMPLEMENTATIONS
        // ═══════════════════════════════════════════════════════════════════════
        this.agentType = 'image';
        this.name = 'imageSearchAgent';
        this.isAgent = true; // Mark as agent-as-tool
        this.createsUndoCheckpoint = false; // Read-only search agent
        // Tools this agent can use (submitResults is auto-injected by initializeAgent)
        this.toolCalls = [
            new SearchBrandfetchTool(),
            new RetrieveBrandfetchTool(),
            new SearchWikimediaTool(),
            new RetrieveWikimediaTool(),
            new SearchPresentationImagesTool(),
            new RetrievePresentationImageTool(),
        ];
        // ═══════════════════════════════════════════════════════════════════════
        // STREAMING FIELD CONFIGURATION (for Input Parameters UI)
        // ═══════════════════════════════════════════════════════════════════════
        /** Define fields to extract during streaming for progressive UI updates */
        this.fieldsToCheck = [
            { name: 'documentId', type: 'string', list_status: false, order: 2 },
        ];
        /** Define list fields - items committed individually as they stream in */
        this.listsToCheck = [
            {
                name: 'requirements',
                type: 'array',
                item_type: 'object',
                list_status: true,
                order: 1,
                check_sub_field: 'requirement' // Item ready when 'requirement' field exists
            },
        ];
        // Enable infrastructure for agent-as-tool pattern
        this.useThreadManagement = true;
        this.useTaskTracking = true;
        this.useContextResolution = true;
        // Use standard submitResults pattern (with validation in processSubmittedResults)
        this.useSubmitResultsPattern = true;
        // Enable streaming for real-time UI updates
        this.stream = true;
        // Allow longer responses for image analysis
        // maxResponseTokens: inherits 0 from base → adapter uses model maximum
        // Track gathered assets during execution
        this.gatheredAssets = new Map();
        this.description = new ToolDescription(this.name, this.systemPromptDescription, ImageSearchParamsSchema, {
            briefDescription: "Search for logos, brand colors, and images",
            whenToUse: [
                "Finding company logos",
                "Getting brand color palettes",
                "Finding executive headshots or product images",
            ],
            exampleUsage: '{"requirements": [{"id": "A", "requirement": "Microsoft logo", "preferredShape": "horizontal", "preferredBackground": "light_bg"}]}',
            commonMistakes: [
                "Not specifying image type clearly",
                "Forgetting to set preferredShape/preferredBackground when placement context is known",
            ],
            capabilities: [
                "Company logos from Brandfetch",
                "Brand colors",
                "Wikimedia images",
                "Presentation images",
            ],
        });
        // Set paramsSchema for agent-as-tool pattern
        this.paramsSchema = ImageSearchParamsSchema;
        // Initialize agent (injects submitResults tool)
        this.initializeAgent();
    }
    /**
     * Generate system prompt for the Image Search agent
     * Uses the new standardized 5-section prompt structure
     */
    generateSystemPrompt(resolvedContext) {
        return buildImageSearchSystemPrompt({ resolvedContext, tools: this.toolCalls });
    }
    // ═══════════════════════════════════════════════════════════════════════
    // TOOL RESULT HANDLING
    // ═══════════════════════════════════════════════════════════════════════
    /**
     * Handle tool results - store retrieved assets
     */
    onToolResult(toolName, result) {
        // Handle retrieve tool results - store the assets
        if (toolName.startsWith('retrieve_') && result) {
            const retrieveResult = result;
            if (retrieveResult.success && Array.isArray(retrieveResult.assets) && retrieveResult.assets.length > 0) {
                for (const asset of retrieveResult.assets) {
                    const storedData = {
                        id: asset.assetId,
                        note: asset.note,
                        metadata: asset.metadata,
                        createdAt: new Date().toISOString(),
                    };
                    this.gatheredAssets.set(asset.assetId, storedData);
                    GlobalStateManager.getInstance().addOrUpdateContext(asset.assetId, storedData);
                    console.log(`[ImageSearchAgent] Stored asset: ${asset.assetId}`);
                }
                return;
            }
            if (retrieveResult.success && retrieveResult.assetId) {
                const storedData = {
                    id: retrieveResult.assetId,
                    note: retrieveResult.note || '',
                    metadata: retrieveResult.metadata || {},
                    createdAt: new Date().toISOString(),
                };
                this.gatheredAssets.set(retrieveResult.assetId, storedData);
                GlobalStateManager.getInstance().addOrUpdateContext(retrieveResult.assetId, storedData);
                console.log(`[ImageSearchAgent] Stored asset: ${retrieveResult.assetId}`);
            }
        }
    }
    // ═══════════════════════════════════════════════════════════════════════
    // HOOKS - Override base class hooks
    // ═══════════════════════════════════════════════════════════════════════
    /**
     * Prepare for execution - clear previous state
     */
    async beforeExecute(context) {
        console.log(`[ImageSearchAgent] Starting execution with ${context.params.requirements.length} requirements`);
        if (context.params.documentId !== undefined) {
            console.log(`[ImageSearchAgent] documentId provided: ${context.params.documentId}`);
        }
        this.gatheredAssets.clear();
    }
    /**
     * Build final result with context ID note for orchestrator.
     * Also appends mismatch warnings if any assets didn't match preferences.
     */
    buildFinalResult(results, threadId, tokensUsed, summary) {
        if (results.length === 0) {
            return this.buildPartialResult(results, threadId, tokensUsed, summary);
        }
        const result = this.buildResult(results, threadId, tokensUsed, summary);
        result._contextIdNote = "The assetId values (logo_*, color_*, wikimedia_*, or composite IDs like deck_abc|slide_257|shape_42) can be passed to execution agents for image insertion. Store in Asset DB for retrieval.";
        // Append mismatch warnings so orchestrator sees them in the summary
        const mismatchWarnings = [];
        for (const item of results) {
            const assets = item.relevantResults?.assets || [];
            for (const asset of assets) {
                if (asset.preferenceMismatch) {
                    const parts = [];
                    if (asset.preferenceMismatch.shapeMismatch) {
                        parts.push(`shape: wanted ${asset.preferenceMismatch.requestedShape}, got ${asset.preferenceMismatch.actualShape}`);
                    }
                    if (asset.preferenceMismatch.backgroundMismatch) {
                        parts.push(`background: ${asset.preferenceMismatch.requestedBackground}, got variant ${asset.preferenceMismatch.actualVariant}`);
                    }
                    mismatchWarnings.push(`[${item.requirementId}] ${asset.assetId}: ${parts.join('; ')}`);
                }
            }
        }
        if (mismatchWarnings.length > 0) {
            const warningText = `\n⚠️ PREFERENCE MISMATCHES (${mismatchWarnings.length}): The following assets did not match requested preferences:\n${mismatchWarnings.join('\n')}`;
            result.summary = (result.summary || '') + warningText;
        }
        return result;
    }
    // ═══════════════════════════════════════════════════════════════════════
    // RESULT PROCESSING (Submit Results Pattern)
    // ═══════════════════════════════════════════════════════════════════════
    /**
     * Process submitted results from submitResults tool.
     *
     * This is the primary path - validates that asset IDs exist and saves images to disk.
     */
    async processSubmittedResults(submittedResults, _params, context) {
        const results = [];
        const contextResolver = ContextResolver.getInstance();
        // Debug: Log what's in the image cache
        const imageIds = contextResolver.getImageIds();
        console.log(`[ImageSearchAgent] Image cache contains ${imageIds.length} images:`, imageIds);
        // Build a lookup of requirement preferences for mismatch checking
        const requirementPrefs = new Map();
        for (const req of context.params.requirements) {
            requirementPrefs.set(req.id, {
                preferredShape: req.preferredShape,
                preferredBackground: req.preferredBackground,
            });
        }
        for (const submitted of submittedResults.results) {
            context.cancellationToken.throwIfCancelled();
            const validAssets = [];
            const prefs = requirementPrefs.get(submitted.requirementId);
            // Extract assets from relevantResults (format: { assets: [{ id, note }] })
            const assetsField = submitted.relevantResults?.assets;
            const assetList = Array.isArray(assetsField) ? assetsField : [];
            console.log(`[ImageSearchAgent] Processing requirement ${submitted.requirementId} with ${assetList.length} assets`);
            for (const asset of assetList) {
                const assetId = asset.id;
                const assetNote = asset.note || '';
                // Validate asset exists in context (either as content or image)
                const hasContent = contextResolver.has(assetId);
                const hasImage = contextResolver.hasImage(assetId);
                // Also check gatheredAssets (local tracking)
                const storedAsset = this.gatheredAssets.get(assetId);
                console.log(`[ImageSearchAgent] Checking asset ${assetId}: hasContent=${hasContent}, hasImage=${hasImage}, inGathered=${!!storedAsset}`);
                if (hasContent || hasImage || storedAsset) {
                    // Check preference mismatches against asset metadata
                    const mismatch = this.checkPreferenceMismatch(assetId, storedAsset, prefs);
                    const imageAsset = {
                        assetId: assetId,
                        note: assetNote || storedAsset?.note || '',
                        source: this.getSourceFromAssetId(assetId),
                        type: this.getTypeFromAssetId(assetId),
                    };
                    if (mismatch) {
                        imageAsset.preferenceMismatch = mismatch;
                        console.log(`  ⚠️ ${assetId}: preference mismatch:`, JSON.stringify(mismatch));
                    }
                    validAssets.push(imageAsset);
                    console.log(`  ✓ ${assetId}: ${assetNote.slice(0, 50)}${assetNote.length > 50 ? '...' : ''}`);
                    // Save image assets to local filesystem for debugging/inspection
                    if (hasImage) {
                        const imageData = contextResolver.getImage(assetId);
                        if (imageData) {
                            const base64Preview = imageData.base64.substring(0, 50);
                            console.log(`    📸 Image data: mimeType=${imageData.mimeType}, base64 starts with: ${base64Preview}...`);
                            try {
                                const saveResult = await backendClient.saveAssetToFile(assetId, imageData.base64, imageData.mimeType);
                                if (saveResult.success) {
                                    console.log(`    💾 Saved to: ${saveResult.filePath}`);
                                }
                                else {
                                    console.warn(`    ⚠️ Failed to save: ${saveResult.error}`);
                                }
                            }
                            catch (saveError) {
                                console.warn(`    ⚠️ Save error: ${saveError}`);
                            }
                        }
                    }
                }
                else {
                    console.warn(`  ✗ ${assetId}: NOT FOUND in context - skipping`);
                    // Log similar IDs for debugging
                    const prefix = assetId.split('_')[0];
                    const similarIds = imageIds.filter(id => id.startsWith(prefix));
                    if (similarIds.length > 0) {
                        console.warn(`    Similar IDs in cache: ${similarIds.slice(0, 5).join(', ')}`);
                    }
                }
            }
            results.push({
                requirementId: submitted.requirementId,
                relevantResults: { assets: validAssets },
                rationale: submitted.rationale,
            });
            console.log(`[ImageSearchAgent] Requirement ${submitted.requirementId}: ${validAssets.length} valid assets`);
        }
        return results;
    }
    /**
     * Fallback: Process results when submitResults is not called
     */
    async processResults(responseContent, params, context) {
        console.warn('[ImageSearchAgent] processResults called - LLM did not use submitResults tool');
        const results = [];
        const allAssetIds = Array.from(this.gatheredAssets.keys());
        for (const req of params.requirements) {
            context.cancellationToken.throwIfCancelled();
            // Try to find asset IDs mentioned in response for this requirement
            const mentionedIds = this.extractAssetIdsFromResponse(responseContent, allAssetIds);
            const relevantIds = mentionedIds.length > 0 ? mentionedIds : allAssetIds;
            const assets = [];
            for (const assetId of relevantIds) {
                const storedAsset = this.gatheredAssets.get(assetId);
                if (storedAsset) {
                    assets.push({
                        assetId: storedAsset.id,
                        note: storedAsset.note,
                        source: this.getSourceFromAssetId(storedAsset.id),
                        type: this.getTypeFromAssetId(storedAsset.id),
                    });
                }
            }
            results.push({
                requirementId: req.id,
                relevantResults: { assets },
                rationale: `Found ${assets.length} assets for: ${req.requirement}`,
            });
            console.log(`[ImageSearchAgent] Requirement ${req.id}: ${assets.length} assets (fallback path)`);
        }
        return results;
    }
    // ═══════════════════════════════════════════════════════════════════════
    // PREFERENCE MISMATCH CHECKING
    // ═══════════════════════════════════════════════════════════════════════
    /**
     * Check whether an asset matches the requirement's shape/background preferences.
     * Returns a PreferenceMismatch object if there's a mismatch, or undefined if all OK.
     */
    checkPreferenceMismatch(assetId, storedAsset, prefs) {
        if (!prefs)
            return undefined;
        const { preferredShape, preferredBackground } = prefs;
        // Skip if no preferences set or both are "any"
        if ((!preferredShape || preferredShape === 'any') &&
            (!preferredBackground || preferredBackground === 'any')) {
            return undefined;
        }
        // Get metadata from stored asset (gathered during retrieval)
        const metadata = storedAsset?.metadata;
        // Also check ContextResolver for stored content metadata
        if (!metadata) {
            const contextResolver = ContextResolver.getInstance();
            const resolved = contextResolver.resolve(assetId);
            if (!resolved)
                return undefined;
            const content = resolved.content;
            if (!content?.metadata)
                return undefined;
        }
        const mismatch = {};
        let hasMismatch = false;
        // Check shape preference
        if (preferredShape && preferredShape !== 'any' && metadata?.shape) {
            const actualShape = metadata.shape; // "square" | "horizontal" | "vertical"
            if (preferredShape === 'square' && actualShape !== 'square') {
                mismatch.shapeMismatch = true;
                mismatch.requestedShape = 'square';
                mismatch.actualShape = actualShape;
                hasMismatch = true;
            }
            else if (preferredShape === 'horizontal' && actualShape !== 'horizontal') {
                mismatch.shapeMismatch = true;
                mismatch.requestedShape = 'horizontal';
                mismatch.actualShape = actualShape;
                hasMismatch = true;
            }
        }
        // Check background preference
        // "light_bg" means logo on light bg → we want dark/colored logos
        // "dark_bg" means logo on dark bg → we want light/white logos
        if (preferredBackground && preferredBackground !== 'any') {
            // Try to determine variant from stored content in ContextResolver
            let variant = undefined;
            if (metadata && 'variant' in metadata) {
                variant = metadata.variant;
            }
            if (!variant) {
                const contextResolver = ContextResolver.getInstance();
                const resolved = contextResolver.resolve(assetId);
                const content = resolved?.content;
                if (content?.variant) {
                    variant = content.variant;
                }
            }
            if (variant) {
                const isLightVariant = variant.startsWith('light_');
                const isDarkVariant = variant.startsWith('dark_');
                if (preferredBackground === 'light_bg' && isLightVariant) {
                    // Light variant on light background → low visibility
                    mismatch.backgroundMismatch = true;
                    mismatch.requestedBackground = 'light_bg (need dark/colored logo)';
                    mismatch.actualVariant = variant;
                    hasMismatch = true;
                }
                else if (preferredBackground === 'dark_bg' && isDarkVariant) {
                    // Dark variant on dark background → low visibility
                    mismatch.backgroundMismatch = true;
                    mismatch.requestedBackground = 'dark_bg (need light/white logo)';
                    mismatch.actualVariant = variant;
                    hasMismatch = true;
                }
            }
        }
        return hasMismatch ? mismatch : undefined;
    }
    // ═══════════════════════════════════════════════════════════════════════
    // HELPER METHODS
    // ═══════════════════════════════════════════════════════════════════════
    /**
     * Determine source from asset ID prefix
     *
     * Supports:
     * - Simple prefixed IDs: logo_*, color_*, wikimedia_*
     * - Composite IDs: deck_abc|slide_257|shape_42 (contain "|")
     */
    getSourceFromAssetId(assetId) {
        // Composite IDs (contain "|") are from presentation
        if (assetId.includes('|')) {
            return 'presentation';
        }
        if (assetId.startsWith('logo_') || assetId.startsWith('color_')) {
            return 'brandfetch';
        }
        if (assetId.startsWith('wikimedia_')) {
            return 'wikimedia';
        }
        return 'presentation'; // default to presentation for unknown formats
    }
    /**
     * Determine type from asset ID prefix
     *
     * Supports:
     * - Simple prefixed IDs: logo_*, color_*, wikimedia_*
     * - Composite IDs: deck_abc|slide_257|shape_42 (contain "|")
     */
    getTypeFromAssetId(assetId) {
        // Composite IDs (contain "|") are deck images
        if (assetId.includes('|')) {
            return 'deck_image';
        }
        if (assetId.startsWith('logo_')) {
            return 'logo';
        }
        if (assetId.startsWith('color_')) {
            return 'brand_colors';
        }
        return 'image';
    }
    /**
     * Extract asset IDs mentioned in the LLM response
     *
     * Supports both simple prefixed IDs and composite IDs.
     */
    extractAssetIdsFromResponse(response, validAssetIds) {
        const mentioned = [];
        // Look for known prefixes and composite ID patterns
        const assetIdPatterns = [
            /logo_[a-z0-9]+/g,
            /color_[a-z0-9]+/g,
            /wikimedia_[a-z0-9]+/g,
            // Composite IDs: deck_id:slide_id:shape_id (presentation images)
            /deck_[a-z0-9\-]+:slide_[a-z0-9_]+:shape_[a-z0-9_]+/gi,
        ];
        for (const pattern of assetIdPatterns) {
            const matches = response.match(pattern) || [];
            for (const match of matches) {
                if (validAssetIds.includes(match) && !mentioned.includes(match)) {
                    mentioned.push(match);
                }
            }
        }
        return mentioned;
    }
}
// ═══════════════════════════════════════════════════════════════════════════
// EXPORTS
// ═══════════════════════════════════════════════════════════════════════════
export default ImageSearchAgent;
